import React, { useState, useEffect, useRef } from "react";
import { collection, addDoc, serverTimestamp, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { X, Plus } from "lucide-react";
import CampiIndirizzo from "./CampiIndirizzo";
import { PARTI_VUOTE, erroriIndirizzo, componiIndirizzo } from "../utils/indirizzi";
import { prossimoProgressivo } from "../utils/contatoriUtils";

const AZIONI = [
  { id:"attivita",   emoji:"✔️", label:"Nuova attività",    modal:true  },
  { id:"commessa",   emoji:"📁", label:"Nuova commessa",    modal:false },
  { id:"cliente",    emoji:"🏢", label:"Nuovo cliente",     modal:true  },
  { id:"richiesta",  emoji:"🛒", label:"Nuova richiesta"  },
  { id:"scadenza",   emoji:"📅", label:"Nuova scadenza",    modal:true  },
  { id:"nota-spese", emoji:"💳", label:"Nota spese",        modal:false },
  { id:"rapportino", emoji:"📋", label:"Nuovo rapportino",  modal:false },
];

// ── Modal Nuova Attività ────────────────────────────────────
function ModalAttivita({ onClose, orgId, currentUser }) {
  const { addToast } = useToast();
  const [form, setForm] = useState({ title:"", projectId:"", assignedTo:"", dueDate:"", priority:"media" });
  const [projects, setProjects] = useState([]);
  const [utenti, setUtenti] = useState([]);

  useEffect(()=>{
    getDocs(query(collection(db,"projects"),where("organizationId","==",orgId))).then(s=>setProjects(s.docs.map(d=>({id:d.id,...d.data()})).filter(p=>!p.archived&&!p.isParent&&p.statoCommessa!=="chiusa")));
    getDocs(query(collection(db,"users"),where("organizationId","==",orgId),where("enabled","==",true))).then(s=>setUtenti(s.docs.map(d=>({uid:d.id,...d.data()}))));
  },[orgId]);

  const salva = async () => {
    if(!form.title.trim()){ addToast("Titolo obbligatorio","error"); return; }
    // Trova prima sezione del progetto
    let sectionId = "";
    if(form.projectId){
      const sez = await getDocs(query(collection(db,"sections"),where("projectId","==",form.projectId)));
      sectionId = sez.docs[0]?.id||"";
    }
    const u = utenti.find(u=>u.uid===form.assignedTo);
    await addDoc(collection(db,"tasks"),{
      title:form.title.trim(), status:"aperta", priority:form.priority,
      projectId:form.projectId||"", sectionId,
      organizationId:orgId,
      assignedTo:form.assignedTo||null, assignedName:u?.displayName||null,
      dueDate:form.dueDate||null,
      createdAt:serverTimestamp(), createdBy:currentUser?.uid||"",
    });
    addToast("✅ Attività creata","success");
    onClose();
  };

  return (
    <ModalWrap titolo="✔️ Nuova attività" onClose={onClose} onSalva={salva} labelSalva="Crea attività">
      <Campo label="Titolo *"><input autoFocus className="input" value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} placeholder="Es. Verifica quadro elettrico"/></Campo>
      <Campo label="Commessa">
        <select className="input" value={form.projectId} onChange={e=>setForm(f=>({...f,projectId:e.target.value}))}>
          <option value="">— Nessuna —</option>
          {projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </Campo>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        <Campo label="Assegnato a">
          <select className="input" value={form.assignedTo} onChange={e=>setForm(f=>({...f,assignedTo:e.target.value}))}>
            <option value="">— Nessuno —</option>
            {utenti.map(u=><option key={u.uid} value={u.uid}>{u.displayName||u.email}</option>)}
          </select>
        </Campo>
        <Campo label="Scadenza"><input className="input" type="date" value={form.dueDate} onChange={e=>setForm(f=>({...f,dueDate:e.target.value}))}/></Campo>
      </div>
      <Campo label="Priorità">
        <select className="input" value={form.priority} onChange={e=>setForm(f=>({...f,priority:e.target.value}))}>
          <option value="bassa">Bassa</option>
          <option value="media">Media</option>
          <option value="alta">Alta</option>
          <option value="urgente">Urgente</option>
        </select>
      </Campo>
    </ModalWrap>
  );
}

// ── Modal Nuovo Cliente ─────────────────────────────────────
function ModalCliente({ onClose, orgId, currentUser }) {
  const { addToast } = useToast();
  const [form, setForm] = useState({ ragioneSociale:"", piva:"", codiceFiscale:"", email:"", telefono:"" });
  // indirizzo a pezzi come in Anagrafica Clienti (W438): un indirizzo senza civico o CAP porta
  // navigatore e consegne da un'altra parte
  const [parti, setParti] = useState(PARTI_VUOTE);
  const [erroriInd, setErroriInd] = useState({});

  const salva = async () => {
    if(!form.ragioneSociale.trim()){ addToast("Ragione sociale obbligatoria","error"); return; }
    if(!form.piva.trim()&&!form.codiceFiscale.trim()){ addToast("P.IVA o Codice Fiscale obbligatorio","error"); return; }
    // Controllo duplicati
    const dup = await getDocs(query(collection(db,"clienti"),where("organizationId","==",orgId)));
    const exists = dup.docs.some(d=>{
      const c=d.data();
      return (form.piva&&c.piva===form.piva.trim())||(form.codiceFiscale&&c.codiceFiscale===form.codiceFiscale.trim());
    });
    if(exists){ addToast("Cliente già presente con questa P.IVA o CF","error"); return; }
    const err = erroriIndirizzo(parti);
    setErroriInd(err);
    if(Object.keys(err).length){ addToast("Completa l'indirizzo: via, numero civico, CAP, comune e provincia","error"); return; }
    const p = { via:parti.via.trim(), civico:parti.civico.trim(), cap:parti.cap.trim(), comune:parti.comune.trim(), provincia:parti.provincia.trim().toUpperCase() };
    const progressivo = await prossimoProgressivo(`${orgId}_numeroCliente`, { orgId }, 99);
    const numeroCliente = `CLI-${String(progressivo).padStart(4,"0")}`;
    await addDoc(collection(db,"clienti"),{...form, ragioneSociale:form.ragioneSociale.trim(), indirizzoParti:p, indirizzo:componiIndirizzo(p), stato:"Attivo", numeroCliente, organizationId:orgId, createdAt:serverTimestamp(), createdBy:currentUser?.uid||""});
    addToast(`✅ Cliente creato — N° ${numeroCliente}`,"success");
    onClose();
  };

  return (
    <ModalWrap titolo="🏢 Nuovo cliente" onClose={onClose} onSalva={salva} labelSalva="Crea cliente">
      <Campo label="Ragione sociale *"><input autoFocus className="input" value={form.ragioneSociale} onChange={e=>setForm(f=>({...f,ragioneSociale:e.target.value}))} placeholder="Es. Jcoplastic Srl"/></Campo>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        <Campo label="P.IVA"><input className="input" value={form.piva} onChange={e=>setForm(f=>({...f,piva:e.target.value}))} placeholder="IT12345678901"/></Campo>
        <Campo label="Codice Fiscale"><input className="input" value={form.codiceFiscale} onChange={e=>setForm(f=>({...f,codiceFiscale:e.target.value}))} placeholder="RSSMRA80A01H501U"/></Campo>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        <Campo label="Email"><input className="input" type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder="info@cliente.it"/></Campo>
        <Campo label="Telefono"><input className="input" value={form.telefono} onChange={e=>setForm(f=>({...f,telefono:e.target.value}))} placeholder="+39 081 000000"/></Campo>
      </div>
      <CampiIndirizzo titolo="📍 Indirizzo della sede legale" valore={parti} onChange={x=>{ setParti(x); if(Object.keys(erroriInd).length) setErroriInd(erroriIndirizzo(x)); }} errori={erroriInd}/>
    </ModalWrap>
  );
}

// ── Modal Nuova Scadenza ────────────────────────────────────
function ModalScadenza({ onClose, orgId, currentUser }) {
  const { addToast } = useToast();
  const [form, setForm] = useState({ voce:"", dataScadenza:"", categoria:"Amministrativa", note:"", promemoriaGiorni:30 });

  const CATEGORIE = ["Amministrativa","Fiscale","Assicurativa","Contrattuale","Certificazioni","Personale","Altro"];

  const salva = async () => {
    if(!form.voce.trim()){ addToast("Descrizione obbligatoria","error"); return; }
    if(!form.dataScadenza){ addToast("Data scadenza obbligatoria","error"); return; }
    // Stesso formato delle voci create nello Scadenzario: prima il documento usava altri
    // nomi di campo (voce, orgId) — le Regole Firestore lo rifiutavano e, anche se salvato,
    // lo Scadenzario non lo avrebbe mostrato né avrebbe mandato il promemoria.
    const scheda = form.categoria==="Personale" ? "personale" : form.categoria==="Certificazioni" ? "sicurezza" : "amministrazione";
    await addDoc(collection(db,"scadenzario"),{
      nome:form.voce.trim(), tipo:scheda, organizationId:orgId,
      targa:"", descrizione:"", dataScadenza:form.dataScadenza,
      ricorrenza:"Nessuna", promemoria:true, promemoriaGiorni:Math.max(0,parseInt(form.promemoriaGiorni)||30),
      assegnatoA:"", assegnatoNome:"",
      note:[["Personale","Certificazioni","Amministrativa"].includes(form.categoria)?"":form.categoria, form.note].filter(Boolean).join(" — "),
      completata:false, ordine:999,
      createdAt:serverTimestamp(), updatedAt:serverTimestamp(), createdBy:currentUser?.uid||"",
    });
    addToast("✅ Scadenza creata","success");
    onClose();
  };

  return (
    <ModalWrap titolo="📅 Nuova scadenza" onClose={onClose} onSalva={salva} labelSalva="Crea scadenza">
      <Campo label="Descrizione *"><input autoFocus className="input" value={form.voce} onChange={e=>setForm(f=>({...f,voce:e.target.value}))} placeholder="Es. Rinnovo assicurazione RC"/></Campo>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        <Campo label="Data scadenza *"><input className="input" type="date" value={form.dataScadenza} onChange={e=>setForm(f=>({...f,dataScadenza:e.target.value}))}/></Campo>
        <Campo label="Promemoria (giorni prima)"><input className="input" type="number" min={1} value={form.promemoriaGiorni} onChange={e=>setForm(f=>({...f,promemoriaGiorni:e.target.value}))}/></Campo>
      </div>
      <Campo label="Categoria">
        <select className="input" value={form.categoria} onChange={e=>setForm(f=>({...f,categoria:e.target.value}))}>
          {CATEGORIE.map(c=><option key={c} value={c}>{c}</option>)}
        </select>
      </Campo>
      <Campo label="Note"><input className="input" value={form.note} onChange={e=>setForm(f=>({...f,note:e.target.value}))} placeholder="Note opzionali..."/></Campo>
    </ModalWrap>
  );
}

// ── Helper componenti UI ────────────────────────────────────
function Campo({ label, children }) {
  return (
    <div className="form-group" style={{marginBottom:12}}>
      <label className="form-label" style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>{label}</label>
      {children}
    </div>
  );
}

function ModalWrap({ titolo, onClose, onSalva, labelSalva, children }) {
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:9500,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:460,boxShadow:"0 20px 60px rgba(0,0,0,.2)",animation:"fadeInScale .15s ease"}}
        onClick={e=>e.stopPropagation()}>
        <div style={{background:"linear-gradient(135deg,#1e1b4b,#3730a3)",padding:"16px 20px",borderRadius:"14px 14px 0 0",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <span style={{fontSize:15,fontWeight:800,color:"#fff"}}>{titolo}</span>
          <button onClick={onClose} style={{background:"rgba(255,255,255,.1)",border:"none",borderRadius:6,padding:"4px 8px",cursor:"pointer",color:"#fff",display:"flex"}}>
            <X size={14}/>
          </button>
        </div>
        <div style={{padding:"20px 20px 8px"}}>{children}</div>
        <div style={{padding:"12px 20px 20px",display:"flex",gap:10,justifyContent:"flex-end"}}>
          <button onClick={onClose} style={{padding:"8px 16px",border:"1px solid #e5e7eb",borderRadius:8,background:"#fff",color:"#374151",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
            Annulla
          </button>
          <button onClick={onSalva} style={{padding:"8px 20px",border:"none",borderRadius:8,background:"#7c3aed",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
            {labelSalva}
          </button>
        </div>
      </div>
      <style>{`@keyframes fadeInScale{from{opacity:0;transform:scale(.95)}to{opacity:1;transform:scale(1)}}`}</style>
    </div>
  );
}

// ── Componente principale QuickActions ──────────────────────
export default function QuickActions({ onNavigate }) {
  const { currentUser } = useAuth();
  const { effectiveOrgId } = useOrg();
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState(null);
  const ref = useRef();

  // Shortcut N per aprire
  useEffect(()=>{
    const handler = e => {
      if(e.key==="n"&&!e.ctrlKey&&!e.metaKey&&e.target.tagName!=="INPUT"&&e.target.tagName!=="TEXTAREA"&&e.target.tagName!=="SELECT"){
        setOpen(s=>!s);
      }
      if(e.key==="Escape"){ setOpen(false); setModal(null); }
    };
    window.addEventListener("keydown",handler);
    return()=>window.removeEventListener("keydown",handler);
  },[]);

  // Chiudi cliccando fuori
  useEffect(()=>{
    if(!open) return;
    const handler = e=>{ if(ref.current&&!ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown",handler);
    return()=>document.removeEventListener("mousedown",handler);
  },[open]);

  const handleAzione = (azione) => {
    setOpen(false);
    if(azione.modal){
      setModal(azione.id);
    } else {
      // Naviga alla sezione
      const NAV = {
        "commessa":  ()=>onNavigate({page:"new-project"}),
        "nota-spese":()=>onNavigate({page:"nota-spese"}),
        "rapportino":()=>onNavigate({page:"registro-rapportini"}),
        // La richiesta materiali apre la pagina vera delle NF (numerazione, righe, stati,
        // invio all'Ufficio Acquisti). Il vecchio modulo rapido salvava un documento in un
        // formato diverso che le Regole Firestore rifiutavano.
        "richiesta": ()=>onNavigate({page:"richiedi-nf"}),
      };
      NAV[azione.id]?.();
    }
  };

  return (
    <>
      {/* Pulsante + fisso */}
      {/* più in alto dell'angolo: lì sotto Netlify può mostrare il riquadro «Powered by Netlify» */}
      <div ref={ref} className="nx-flottante" style={{position:"fixed",bottom:76,right:24,zIndex:7000}}>
        {/* Menu azioni */}
        {open && (
          <div style={{
            position:"absolute",bottom:56,right:0,
            background:"#fff",borderRadius:14,
            boxShadow:"0 8px 40px rgba(0,0,0,.18)",
            overflow:"hidden",minWidth:220,
            animation:"slideUp .15s ease"
          }}>
            <div style={{padding:"10px 14px 6px",fontSize:10,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:".06em",borderBottom:"1px solid #f3f4f6"}}>
              Azione rapida <span style={{float:"right",fontWeight:400,color:"#d1d5db"}}>N</span>
            </div>
            {AZIONI.map(a=>(
              <button key={a.id} onClick={()=>handleAzione(a)}
                style={{display:"flex",alignItems:"center",gap:12,width:"100%",padding:"10px 16px",border:"none",background:"#fff",cursor:"pointer",textAlign:"left",fontFamily:"'Inter',sans-serif",fontSize:13,color:"#374151",borderBottom:"1px solid #f9fafb"}}
                onMouseOver={e=>e.currentTarget.style.background="#faf5ff"}
                onMouseOut={e=>e.currentTarget.style.background="#fff"}>
                <span style={{fontSize:18,width:24,textAlign:"center"}}>{a.emoji}</span>
                <span style={{fontWeight:600}}>{a.label}</span>
                {!a.modal&&<span style={{marginLeft:"auto",fontSize:10,color:"#d1d5db"}}>→</span>}
              </button>
            ))}
          </div>
        )}

        {/* Bottone + */}
        <button onClick={()=>setOpen(s=>!s)}
          title="Azioni rapide (N)"
          style={{
            width:52,height:52,borderRadius:"50%",
            background:open?"#5b21b6":"#7c3aed",
            border:"none",cursor:"pointer",
            display:"flex",alignItems:"center",justifyContent:"center",
            boxShadow:"0 4px 20px rgba(124,58,237,.5)",
            transition:"all .2s",
            transform:open?"rotate(45deg)":"rotate(0deg)"
          }}>
          <Plus size={24} color="#fff"/>
        </button>
      </div>

      {/* Modal azioni */}
      {modal==="attivita" && <ModalAttivita onClose={()=>setModal(null)} orgId={effectiveOrgId} currentUser={currentUser}/>}
      {modal==="cliente"  && <ModalCliente  onClose={()=>setModal(null)} orgId={effectiveOrgId} currentUser={currentUser}/>}
      {modal==="scadenza" && <ModalScadenza  onClose={()=>setModal(null)} orgId={effectiveOrgId} currentUser={currentUser}/>}

      <style>{`@keyframes slideUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </>
  );
}
